import { normalizeRecapTeamCode } from "./importRecapScores";

const clamp01 = (value) => Math.min(1, Math.max(0, Number(value) || 0));
const GRID_H = 0.155;
const GRID_W = 0.23;

const hasCoords = (block) =>
  [block?.topLeftX, block?.topLeftY, block?.bottomRightX, block?.bottomRightY].every(
    (value) => Number.isFinite(Number(value))
  );

const asBox = (block) => ({
  topLeftX: Number(block.topLeftX),
  topLeftY: Number(block.topLeftY),
  bottomRightX: Number(block.bottomRightX),
  bottomRightY: Number(block.bottomRightY),
});

export const unionHighlightBoxes = (boxes = []) => {
  const usable = (boxes || []).filter(
    (box) =>
      box &&
      Number.isFinite(Number(box.topLeftX)) &&
      Number.isFinite(Number(box.topLeftY)) &&
      Number.isFinite(Number(box.bottomRightX)) &&
      Number.isFinite(Number(box.bottomRightY))
  );
  if (!usable.length) return null;
  return {
    topLeftX: Math.min(...usable.map((box) => Number(box.topLeftX))),
    topLeftY: Math.min(...usable.map((box) => Number(box.topLeftY))),
    bottomRightX: Math.max(...usable.map((box) => Number(box.bottomRightX))),
    bottomRightY: Math.max(...usable.map((box) => Number(box.bottomRightY))),
  };
};

const pageSizeOf = (page) => {
  const width = Number(page?.dimensions?.width ?? page?.dimensions?.x);
  const height = Number(page?.dimensions?.height ?? page?.dimensions?.y);
  if (width > 1.5 && height > 1.5) return { width, height };
  return null;
};

const normalizeBox = (box, pageSize) => {
  if (!box) return null;
  let topLeftX = Number(box.topLeftX);
  let topLeftY = Number(box.topLeftY);
  let bottomRightX = Number(box.bottomRightX);
  let bottomRightY = Number(box.bottomRightY);
  const max = Math.max(topLeftX, topLeftY, bottomRightX, bottomRightY);
  const pageWidth = Number(pageSize?.width);
  const pageHeight = Number(pageSize?.height);
  if (max > 1.5 && max <= 100) {
    topLeftX /= 100;
    topLeftY /= 100;
    bottomRightX /= 100;
    bottomRightY /= 100;
  } else if (max > 1.5 && pageWidth > 1.5 && pageHeight > 1.5) {
    topLeftX /= pageWidth;
    topLeftY /= pageHeight;
    bottomRightX /= pageWidth;
    bottomRightY /= pageHeight;
  } else if (max > 1.5) {
    return null;
  }
  const left = clamp01(Math.min(topLeftX, bottomRightX));
  const right = clamp01(Math.max(topLeftX, bottomRightX));
  const top = clamp01(Math.min(topLeftY, bottomRightY));
  const bottom = clamp01(Math.max(topLeftY, bottomRightY));
  if (right - left <= 0 || bottom - top <= 0) return null;
  return { topLeftX: left, topLeftY: top, bottomRightX: right, bottomRightY: bottom };
};

const expandRecapTeamBox = (box) => {
  const parsed = normalizeBox(box);
  if (!parsed) return null;
  return {
    topLeftX: clamp01(parsed.topLeftX - 0.006),
    topLeftY: clamp01(parsed.topLeftY - 0.008),
    bottomRightX: clamp01(parsed.bottomRightX + 0.006),
    bottomRightY: clamp01(parsed.bottomRightY + 0.006),
    source: "recap",
  };
};

const boxCenter = (box) => ({
  x: (Number(box.topLeftX) + Number(box.bottomRightX)) / 2,
  y: (Number(box.topLeftY) + Number(box.bottomRightY)) / 2,
});

const boxHeight = (box) => Number(box.bottomRightY) - Number(box.topLeftY);

const sameColumn = (left, right) =>
  Math.abs(boxCenter(left).x - boxCenter(right).x) <= 0.14;

export const codesInRecapContent = (content) => {
  const raw = String(content || "").toUpperCase();
  const found = [];
  const pattern = /BTA\s*USE\s*:?\s*([0-9A-FO]{6,8})/g;
  let match;
  while ((match = pattern.exec(raw))) {
    const code = normalizeRecapTeamCode(match[1]);
    if (code.length >= 6 && !found.includes(code)) found.push(code);
  }
  return found;
};

const sliceBoxForCode = (block, code, codes) => {
  const box = asBox(block);
  if (!codes.length || codes.length === 1 || boxHeight(box) <= 0.07) return box;
  const index = codes.findIndex(
    (item) => item === code || item.startsWith(code) || code.startsWith(item)
  );
  if (index < 0) return box;
  const slice = boxHeight(box) / codes.length;
  return {
    topLeftX: box.topLeftX,
    topLeftY: box.topLeftY + index * slice,
    bottomRightX: box.bottomRightX,
    bottomRightY: box.topLeftY + (index + 1) * slice,
  };
};

const codeAnchorsFromBlocks = (blocks) =>
  (blocks || []).flatMap((block) => {
    const codes = codesInRecapContent(block.content);
    return codes.map((code) => ({
      ...sliceBoxForCode(block, code, codes),
      content: `BTA Use: ${code}`,
      code,
    }));
  });

const codeAboveInColumn = (codeBox, anchors) =>
  (anchors || [])
    .filter(
      (other) =>
        other !== codeBox &&
        sameColumn(other, codeBox) &&
        boxCenter(other).y < boxCenter(codeBox).y - 0.01
    )
    .sort((left, right) => right.bottomRightY - left.bottomRightY)[0] || null;

const boxFromCodeAnchor = (codeBox, anchors) => {
  const height = boxHeight(codeBox);
  if (height >= 0.1) return codeBox;
  const above = codeAboveInColumn(codeBox, anchors);
  const bottom = clamp01(codeBox.bottomRightY + 0.006);
  const naturalTop = above
    ? above.bottomRightY + 0.004
    : bottom - GRID_H;
  const top = Math.max(naturalTop, bottom - GRID_H * 1.1);
  const left = clamp01(codeBox.topLeftX - 0.01);
  const right = clamp01(Math.max(codeBox.bottomRightX + 0.01, left + GRID_W));
  return {
    topLeftX: left,
    topLeftY: clamp01(top),
    bottomRightX: right,
    bottomRightY: bottom,
  };
};

const clusterNameHits = (nameHits) => {
  const sorted = [...(nameHits || [])].sort(
    (left, right) =>
      left.topLeftY - right.topLeftY || left.topLeftX - right.topLeftX
  );
  const clusters = [];
  sorted.forEach((block) => {
    const host = clusters.find((cluster) =>
      cluster.some((member) => {
        const dy = Math.abs(boxCenter(block).y - boxCenter(member).y);
        const dx = Math.abs(boxCenter(block).x - boxCenter(member).x);
        return dy <= 0.05 && dx <= 0.16;
      })
    );
    if (host) host.push(block);
    else clusters.push([block]);
  });
  return clusters;
};

const clusterNameScore = (cluster, names) => {
  const found = new Set();
  (cluster || []).forEach((block) => {
    const text = normalizeName(block.content);
    (names || []).forEach((name) => {
      if (text.includes(name)) found.add(name);
    });
  });
  return found.size;
};

const pickNameCluster = (nameHits, names) => {
  const clusters = clusterNameHits(nameHits);
  if (!clusters.length) return [];
  clusters.sort((left, right) => {
    const score = clusterNameScore(right, names) - clusterNameScore(left, names);
    if (score) return score;
    const areaOf = (cluster) => {
      const union = unionHighlightBoxes(cluster);
      if (!union) return Number.POSITIVE_INFINITY;
      return (
        (union.bottomRightX - union.topLeftX) *
        (union.bottomRightY - union.topLeftY)
      );
    };
    return areaOf(left) - areaOf(right);
  });
  return clusters[0];
};

const normalizeName = (value) =>
  String(value || "")
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, " ")
    .trim();

export const recapTeamBoundingBox = ({
  pages = [],
  teamCode,
  bowlerNames = [],
} = {}) => {
  const code = normalizeRecapTeamCode(teamCode);
  const names = (bowlerNames || []).map(normalizeName).filter((name) => name.length >= 4);
  const blocks = (pages || []).flatMap((page) => {
    const pageSize = pageSizeOf(page);
    return (page.blocks || [])
      .filter(hasCoords)
      .map((block) => {
        const box = normalizeBox(asBox(block), pageSize);
        if (!box) return null;
        return { ...box, content: block.content };
      })
      .filter(Boolean);
  });
  const anchors = codeAnchorsFromBlocks(blocks);
  const codeBox = code
    ? unionHighlightBoxes(anchors.filter((anchor) => anchor.code === code))
    : null;

  if (codeBox) {
    return expandRecapTeamBox(boxFromCodeAnchor(codeBox, anchors));
  }

  const nameHits = names.length
    ? blocks.filter((block) => {
        const text = normalizeName(block.content);
        return names.some((name) => text.includes(name));
      })
    : [];
  if (!nameHits.length) return null;
  const cluster = unionHighlightBoxes(pickNameCluster(nameHits, names));
  if (!cluster) return null;
  if (boxHeight(cluster) > GRID_H * 1.15) {
    return expandRecapTeamBox({
      ...cluster,
      topLeftY: cluster.bottomRightY - GRID_H,
    });
  }
  return expandRecapTeamBox(cluster);
};
